import { Box, IconButton, Tooltip } from "@mui/material";
import { Cancel, CheckCircle, DescriptionOutlined, ErrorOutline, GetApp, Image, Link, Lock, PriorityHigh, Warning } from "@mui/icons-material";
import clsx from "clsx";
import OfflineFileWrapper from "../../components/OfflineFileWrapper";
import { LOCAL_STORAGE_GRID_KEYS } from "../../_constants";
import { formatDate } from "../../_helpers";
import { getGridContextMenuList } from "../../_helpers/getGridContextMenuList";
import { HoverImage, LabelColumnV2 } from "../../components/DataGridV2/DataColumnsV2";
import { URLS } from "../../apis/urls";

export const getAssignmentColumns = ({
    t,
    keyPrefix,
    i18n,
    apiRef,
    classes,
    wbProjectInfo,
    objectModalListHandler,
    followUpAssignmentsHandler,
    wbStatusOptions,
    extraInformationOptions
}) => {
    const getOptions = getGridContextMenuList({
        filterKey: LOCAL_STORAGE_GRID_KEYS.ASSIGNMENTS_COLUMN_FILTER[keyPrefix],
        sortKey: LOCAL_STORAGE_GRID_KEYS.ASSIGNMENTS_COLUMN_SORT[keyPrefix],
        i18n,
        t,
        apiRef,
        mode: "client",
        getValueForFilter: (rowData, extraData) => {
            let value = ""
            switch (extraData.fieldName) {
                case "repairRequests":
                    value = rowData.repairRequests?.toString()
                    break;
                default:
                    value = rowData[extraData.fieldName]
                    break;

            }
            return value
        },
        getValueForCopy: (rowData, extraData) => {
            let text = ""
            switch (extraData.fieldName) {
                case "internalStatus":
                    text = t(`wkb.assignment.internalStatus.${rowData?.internalStatus}`)
                    break;
                case "startDate":
                    text = rowData?.startDate ? formatDate(new Date(rowData.startDate), false, '-') : ""
                    break;
                case "endDate":
                    text = rowData?.endDate ? formatDate(new Date(rowData.endDate), false, '-') : ""
                    break;
                case "wbReceivedDate":
                    text = rowData?.wbReceivedDate ? formatDate(new Date(rowData?.wbReceivedDate)) : ""
                    break;
                default:
                    text = rowData?.[extraData?.fieldName]
                    break;
            }
            return text
        }
    })

    return [
        {
            field: "hasPriority",
            headerName: 'hasPriority',
            download: false,
            maxWidth: 45,
            minWidth: 45,
            forceApplyWidth: true,
            resizable: false,
            sortable: false,
            flex: 1,
            type: "boolean",
            renderHeader: () => (
                <Box className={classes.iconContainer} sx={{
                    mr: 0.6
                }}>
                    <PriorityHigh PriorityHigh color='secondary' className={classes.icon} />
                </Box>
            ),
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                return (
                    <LabelColumnV2
                        label={<PriorityHigh color="secondary" />}
                        cellSubMenuOptions={{ options: getOptions({ field: "hasPriority", isQuickFilterDisplay: typeof rowData.hasPriority === "boolean", }), extraData: { fieldName: "hasPriority", type: 'boolean' }, }}
                        className={classes.iconContainer}
                        request={rowData}
                    />
                )
            },
        },
        {
            download: false,
            field: "hasError",
            headerName: 'hasError',
            maxWidth: 45,
            minWidth: 45,
            forceApplyWidth: true,
            resizable: false,
            sortable: false,
            flex: 1,
            type: "boolean",
            renderHeader: () => (
                <Box className={classes.iconContainer} sx={{
                    mr: 0.6
                }}>
                    <ErrorOutline fontSize="small" color="error" />
                </Box>
            ),
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                const { surveyingMomentDetails } = rowData;
                return rowData.hasError ? (
                    <LabelColumnV2
                        label={<Tooltip title={t("wkb.objectIsNotLinked")}>
                            <IconButton
                                disabled={!wbProjectInfo || (wbProjectInfo && wbProjectInfo.archivedAt)}
                                className={classes.iconButton}
                                onClick={(e) => objectModalListHandler(e, surveyingMomentDetails.buildingId, surveyingMomentDetails.objectId)}
                                size="large">
                                <ErrorOutline className={classes.colorWarning} />
                            </IconButton>
                        </Tooltip>}
                        className={classes.iconContainer}
                        cellSubMenuOptions={{ options: getOptions({ field: "hasError", isQuickFilterDisplay: typeof rowData.hasError === "boolean", }), extraData: { fieldName: "hasError", type: 'boolean' }, }}
                        request={rowData}
                    />
                ) : null;
            },
        },
        {
            download: false,
            field: "hasDownload",
            headerName: 'hasDownload',
            maxWidth: 45,
            minWidth: 45,
            forceApplyWidth: true,
            resizable: false,
            sortable: false,
            flex: 1,
            type: "singleSelect",
            valueOptions: extraInformationOptions,
            renderHeader: () => (
                <Box className={classes.iconContainer} sx={{
                    mr: 0.6
                }}>
                    <GetApp color='primary' className={classes.tableIcon} />
                </Box>
            ),
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";

                let icon = null;
                switch (rowData.hasDownload) {
                    case "download":
                        icon = <GetApp color="primary" />;
                        break;
                    case "locked":
                        icon = <Lock color="primary" />;
                        break;
                    case "notdownload":
                        icon = '';
                        break;
                    default:
                        icon = null;
                }

                return icon ? (
                    <LabelColumnV2
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {icon}
                            </Box>
                        }
                        cellSubMenuOptions={{
                            options: getOptions({ field: "hasDownload", isQuickFilterDisplay: icon }),
                            extraData: { fieldName: "hasDownload", type: "singleSelect" },
                        }}
                        className={classes.iconContainer}
                        request={rowData}
                    />
                ) : null;
            },

        },
        {
            download: false,
            field: "isCracked",
            headerName: 'isCracked',
            maxWidth: 45,
            minWidth: 45,
            forceApplyWidth: true,
            resizable: false,
            sortable: false,
            flex: 1,
            type: "boolean",
            renderHeader: () => (
                <Box className={classes.iconContainer} sx={{
                    mr: 0.6
                }}>
                    <Warning fontSize="small" color="error" className={classes.tableIcon} />
                </Box>
            ),
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";

                return rowData.isCracked ? (
                    <LabelColumnV2
                        label={<Warning color="primary" />}
                        cellSubMenuOptions={{ options: getOptions({ field: "isCracked", isQuickFilterDisplay: Boolean(rowData.isCracked), }), extraData: { fieldName: "isCracked", type: 'boolean' }, }}
                        className={classes.iconContainer}
                        request={rowData}
                    />
                ) : null;
            },
        },


        {
            download: false,
            print: true,
            field: "assignmentAttachments",
            headerName: t("general.image"),
            flex: 1,
            minWidth: 60,
            renderHeader: () => (
                <Box sx={{ textAlign: "center" }}>
                    <Image />
                </Box>
            ),
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData || !rowData.assignmentAttachments || !rowData.assignmentAttachments.length) return "";
                const imageId = rowData.assignmentAttachments[0].id;
                return (
                    <OfflineFileWrapper id={imageId} url={imageId ? URLS.GET_ASSIGNMENT_ATTACHMENT_THUMBNAIL + imageId : rowData.assignmentAttachments[0]}>
                        <HoverImage defaultSize={20} imageId={imageId} index={params.id} prefixImageUrl={ URLS.GET_ASSIGNMENT_ATTACHMENT_THUMBNAIL} />
                    </OfflineFileWrapper>
                )
            },
        },
        {
            download: true,
            print: true,
            field: "moment",
            headerName: t("wkb.surveyingMoment"),
            flex: 1,
            minWidth: 90,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                return (
                    <LabelColumnV2
                        label={rowData.moment}
                        cellSubMenuOptions={{ options: getOptions({ field: "moment", isQuickFilterDisplay: Boolean(rowData.moment) }), extraData: { fieldName: "moment" } }}
                        request={rowData}
                    />
                );
            },
        },
        {
            download: true,
            print: true,
            field: "object",
            headerName: t("general.object"),
            flex: 1,
            minWidth: 90,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                return (
                    <LabelColumnV2
                        label={rowData.object}
                        cellSubMenuOptions={{ options: getOptions({ field: "object", isQuickFilterDisplay: Boolean(rowData.object) }), extraData: { fieldName: "object" } }}
                        request={rowData}
                    />
                );
            },
        },
        {
            download: true,
            print: true,
            field: "controlPointDesc",
            headerName: t('general.description'),
            flex: 1,
            minWidth: 90,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                const { linkedAssignment, controlPointDesc } = rowData;
                return (
                    <LabelColumnV2
                        label={controlPointDesc}
                        tooltipLabel={controlPointDesc}
                        Icon={Link}
                        showIcon={linkedAssignment}
                        clickable
                        onClick={(e) => followUpAssignmentsHandler(e, linkedAssignment, 'linkedAssignment')}
                        cellSubMenuOptions={{ options: getOptions({ field: "controlPointDesc", isQuickFilterDisplay: Boolean(rowData.controlPointDesc) }), extraData: { fieldName: "controlPointDesc" } }}
                        request={rowData}
                    />
                );
            },
        },
        {
            download: true,
            print: true,
            field: "text",
            headerName: t("wkb.assignments.label"),
            flex: 1,
            minWidth: 90,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                return (
                    <LabelColumnV2
                        label={rowData.text}
                        cellSubMenuOptions={{ options: getOptions({ field: "text", isQuickFilterDisplay: Boolean(rowData.text) }), extraData: { fieldName: "text" } }}
                        request={rowData}
                    />
                );
            },
        },
        {
            download: true,
            print: true,
            field: "remark",
            headerName: t("general.remarks"),
            flex: 1,
            minWidth: 160,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";

                return (
                    <LabelColumnV2
                        label={rowData.remark}
                        tooltipLabel={rowData.remark}
                        cellSubMenuOptions={{ options: getOptions({ field: "remark", isQuickFilterDisplay: Boolean(rowData?.remark) }), extraData: { fieldName: "remark" } }}
                        request={rowData}
                    />
                );
            },
        },
        {
            download: true,
            print: true,
            field: "answer",
            headerName: t('wkb.assignment.answer.label'),
            flex: 1,
            minWidth: 100,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                let answer = {};
                if ((rowData.answer && rowData.answerOptions)) {
                    answer = rowData.answerOptions.find(answer => answer.answerOption === rowData.answer) || {};
                }
                if (!answer.answerOptionLabel) return "";
                return (
                    <>
                        <span className={clsx(classes.assignmentsCardButton, classes.content, classes[answer.answerOptionColor])} >{t(answer.answerOptionLabel)}</span>
                        <div className={classes.spacer}>{t(answer.answerOptionLabel)}</div>
                    </>
                );
            },
        },
        {
            field: "startDate",
            headerName: t('general.startDate'),
            flex: 1,
            minWidth: 90,
            type: "date",
            valueGetter: (params) => {
                if (!params) return "";
                return new Date(params);
            },
            download: true,
            renderCell: (params) => {
                const rowData = params.row;
                const startDate = rowData.startDate ? formatDate(new Date(rowData.startDate), false, '-') : '';
                return <LabelColumnV2
                    label={startDate}
                    cellSubMenuOptions={{ options: getOptions({ field: "startDate", isQuickFilterDisplay: rowData?.startDate }), extraData: { fieldName: "startDate", type: "date" } }}
                    request={rowData}
                />
            },
        },
        {
            field: "endDate",
            headerName: t('general.endDate'),
            flex: 1,
            minWidth: 90,
            type: "date",
            valueGetter: (params) => {
                if (!params) return "";
                return new Date(params);
            },
            download: true,
            renderCell: (params) => {
                const rowData = params.row;
                const endDate = rowData.endDate ? formatDate(new Date(rowData.endDate), false, '-') : '';
                return <LabelColumnV2
                    label={endDate}
                    cellSubMenuOptions={{ options: getOptions({ field: "endDate", isQuickFilterDisplay: rowData?.endDate }), extraData: { fieldName: "endDate", type: "date" } }}
                    request={rowData}
                />
            },
        },
        {
            download: true,
            print: true,
            field: "repairRequests",
            headerName: t("general.repairRequests"),
            flex: 1,
            minWidth: 90,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                return (
                    <LabelColumnV2
                        label={rowData.repairRequests}
                        cellSubMenuOptions={{ options: getOptions({ field: "repairRequests", isQuickFilterDisplay: Boolean(rowData.repairRequests.toString()) }), extraData: { fieldName: "repairRequests" } }}
                        request={rowData}
                    />
                );
            },
        },
        {
            download: true,
            print: true,
            field: "responsiblePersonRelationName",
            headerName: t('wkb.executive'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                return (
                    <LabelColumnV2
                        label={rowData?.responsiblePersonRelationName}
                        cellSubMenuOptions={{ options: getOptions({ field: "responsiblePersonRelationName", isQuickFilterDisplay: Boolean(rowData.responsiblePersonRelationName) }), extraData: { fieldName: "responsiblePersonRelationName", } }}
                        request={rowData}
                    />
                );
            },
        },
        {
            download: true,
            print: true,
            field: "relationName",
            headerName: t('wkb.assignment.delegate'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                return (
                    <LabelColumnV2
                        label={rowData?.relationName}
                        cellSubMenuOptions={{ options: getOptions({ field: "relationName", isQuickFilterDisplay: Boolean(rowData.relationName) }), extraData: { fieldName: "relationName", } }}
                        request={rowData}
                    />
                );
            },
        },
        {
            download: true,
            print: true,
            field: "internalStatus",
            headerName: t("wkb.surveyingMoment.internalStatus"),
            type: 'singleSelect',
            valueOptions: [0, 1, 2, 3].map((val) => ({
                value: val,
                label: t(`wkb.assignment.internalStatus.${val}`),
            })),
            flex: 1,
            minWidth: 90,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                const { followUpAssignments, internalStatus } = rowData;
                return (
                    <LabelColumnV2
                        label={t("wkb.assignment.internalStatus." + internalStatus)}
                        tooltipLabel={t("wkb.assignment.internalStatus." + internalStatus)}
                        Icon={followUpAssignments && !!followUpAssignments.length ? DescriptionOutlined : ''}
                        clickable
                        showIcon={followUpAssignments && !!followUpAssignments.length}
                        onClick={(e) => followUpAssignmentsHandler(e, followUpAssignments, 'followUpAssignments')}
                        cellSubMenuOptions={{ options: getOptions({ field: "internalStatus", isQuickFilterDisplay: Boolean(internalStatus.toString()) }), extraData: { fieldName: "internalStatus", type: 'singleSelect' } }}
                        request={rowData}
                    />
                );
            },
        },
        {
            field: "wbReceivedDate",
            headerName: t('wkb.wbReceivedDate'),
            flex: 1,
            minWidth: 90,
            type: "date",
            valueGetter: (params) => {
                if (!params) return "";
                return new Date(params);
            },
            download: true,
            renderCell: (params) => {
                const rowData = params.row;
                return <LabelColumnV2
                    date={rowData.wbReceivedDate}
                    cellSubMenuOptions={{ options: getOptions({ field: "wbReceivedDate", isQuickFilterDisplay: rowData?.wbReceivedDate }), extraData: { fieldName: "wbReceivedDate", type: "date" } }}
                    request={rowData}
                />
            },
        },
        {
            download: true,
            print: true,
            field: 'wbStatus',
            headerName: t("wkb.wbJudgement"),
            type: 'singleSelect',
            valueOptions: wbStatusOptions,
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                const { wbStatus } = rowData;
                return (
                    <LabelColumnV2
                        label={
                            <>
                                <Tooltip title={wbStatus} arrow placement="right">
                                    <span className={clsx(classes.wbStatusIcon, classes.content)}>
                                        {
                                            (wbStatus === "[WB akkoord]" || wbStatus === "Akkoord") ?
                                                <CheckCircle fontSize="small" className={classes.colorSuccess} /> :
                                                (wbStatus === "[WB niet akkoord]" || wbStatus === "Niet akkoord") ?
                                                    <Cancel fontSize="small" color="error" /> :
                                                    (!!wbStatus && wbStatus !== "") ?
                                                        <PriorityHigh fontSize="small" className={classes.warning} /> :
                                                        wbStatus
                                        }
                                        {wbStatus}
                                    </span>
                                </Tooltip>
                                <div className={classes.spacer}>
                                    {
                                        (wbStatus === "[WB akkoord]" || wbStatus === "Akkoord") ?
                                            <CheckCircle fontSize="small" className={classes.colorSuccess} /> :
                                            (wbStatus === "[WB niet akkoord]" || wbStatus === "Niet akkoord") ?
                                                <Cancel fontSize="small" color="error" /> :
                                                (!!wbStatus && wbStatus !== "") ?
                                                    <PriorityHigh fontSize="small" className={classes.warning} /> :
                                                    wbStatus
                                    }
                                    {wbStatus}
                                </div>
                            </>
                        }
                        request={rowData}
                        cellSubMenuOptions={{ options: getOptions({ field: "wbStatus", isQuickFilterDisplay: rowData?.wbStatus }), extraData: { fieldName: "wbStatus", type: "singleSelect" } }}

                    />

                );
            },
        },
        {
            download: true,
            print: true,
            field: "wbJudgmentDate",
            headerName: t("wkb.wbJudgementDate"),
            flex: 1,
            minWidth: 90,
            type: "date",
            valueGetter: (params) => {
                if (!params) return "";
                return new Date(params);
            },
            renderCell: (params) => {
                const rowData = params.row;
                if (!rowData) return "";
                return (
                    <LabelColumnV2
                        date={rowData.wbJudgmentDate}
                        cellSubMenuOptions={{ options: getOptions({ field: "wbJudgmentDate", isQuickFilterDisplay: Boolean(rowData.wbJudgmentDate) }), extraData: { fieldName: "wbJudgmentDate", type: 'date' } }}
                        request={rowData}
                    />
                );
            },
        },
    ];
}

